import dayjs from "dayjs";

import { ClientPhotoType, type ClientPhoto } from "@fitness-app/data-models/entities/Measurement";
import { type PhotoCompareSlotKey } from "./photo-compare-layout";

// TRE-2105 — a single photo pulled out of a `client_photo` measurement (one measurement holds up
// to three: front / back / side), with the date it was taken so the card can label it.

export interface ComparePhoto {
  photoId: string;
  url: string;
  thumbnailUrl?: string;
  type: ClientPhotoType;
  /** `YYYY-MM-DD` of the measurement. */
  eventDate: string;
}

export type ComparePair = Record<PhotoCompareSlotKey, ComparePhoto | null>;

export const PHOTO_TYPE_LABELS: Record<ClientPhotoType, string> = {
  [ClientPhotoType.Front]: "uiCommon:front",
  [ClientPhotoType.Back]: "uiCommon:back-2",
  [ClientPhotoType.Side]: "uiAccount:profile",
};

export const formatPhotoDate = (eventDate: string) => dayjs(eventDate).format("DD.MM.YYYY");

/** Display order of a set's photos: front, side, back — regardless of the order they were added in. */
export const PHOTO_TYPE_ORDER: ClientPhotoType[] = [ClientPhotoType.Front, ClientPhotoType.Side, ClientPhotoType.Back];

const typeRank = (type: ClientPhotoType) => {
  const index = PHOTO_TYPE_ORDER.indexOf(type);
  return index === -1 ? PHOTO_TYPE_ORDER.length : index;
};

/** Stable sort of photo entries into `PHOTO_TYPE_ORDER`. */
export const sortPhotosByType = <T extends { type: ClientPhotoType }>(photos: T[]): T[] =>
  [...photos].sort((a, b) => typeRank(a.type) - typeRank(b.type));

/** Calendar days between the pair's photos (analytics), null until both are chosen. */
export const daysBetween = (pair: ComparePair): number | null =>
  pair.before && pair.after ? Math.abs(dayjs(pair.after.eventDate).diff(dayjs(pair.before.eventDate), "day")) : null;

/** Properties shared by the comparison analytics events. */
export const comparePairProps = (pair: ComparePair) => ({
  daysBetween: daysBetween(pair),
  photoType: pair.before?.type ?? pair.after?.type ?? null,
});

/** Flattens measurements (newest first, as the store keeps them) into individual photos. */
export const flattenPhotos = (measurements: ClientPhoto[]): ComparePhoto[] =>
  measurements
    .filter((measurement) => measurement.id !== "-1")
    .flatMap((measurement) =>
      sortPhotosByType(measurement.data.content ?? []).map((item) => ({
        photoId: item.photoId,
        url: item.url,
        thumbnailUrl: item.thumbnailUrl,
        type: item.type,
        eventDate: measurement.eventDate,
      })),
    );

/**
 * Oldest and newest photo of the same kind — front first, then whichever type has at least two
 * photos. A single photo still fills "after" so the client sees what she is comparing against.
 */
export const pickDefaultPair = (photos: ComparePhoto[]): ComparePair => {
  const order = [ClientPhotoType.Front, ClientPhotoType.Side, ClientPhotoType.Back];
  for (const type of order) {
    const ofType = photos.filter((photo) => photo.type === type);
    if (ofType.length >= 2) {
      return { before: ofType[ofType.length - 1] ?? null, after: ofType[0] ?? null };
    }
  }
  return { before: photos.length > 1 ? (photos[photos.length - 1] ?? null) : null, after: photos[0] ?? null };
};
